import { Loader as GoogleMaps } from "@googlemaps/js-api-loader";
import { activeSiteConfig } from "@config/site/site-config";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";

let googleMapsLoader: GoogleMaps | null = null;
let loadPromise: Promise<typeof google> | null = null;
let isLoaded = false;

// To Do
// Rolling out the new Google Maps API key to all environments, so we can remove the fallback to the old key
// once all tests are complete and the new key is confirmed to be working in all environments.
const API_KEY =
    process.env.NEXT_PUBLIC_PLATFORM_GOOGLE_MAP_API_KEY ||
    process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY;

const getGoogleMapsLoader = (locale?: string): GoogleMaps => {
    if (googleMapsLoader) {
        return googleMapsLoader;
    }

    if (!API_KEY) {
        throw new Error("Google Maps API key is not defined in environment variables.");
    }

    const { country } = activeSiteConfig;

    googleMapsLoader = new GoogleMaps({
        apiKey: API_KEY,
        region: country,
        language: locale,
        id: "google-maps-script",
    });

    return googleMapsLoader;
};

const loadGoogleMaps = async (locale?: string): Promise<typeof google> => {
    if (isLoaded && window.google?.maps) {
        return Promise.resolve(window.google);
    }

    if (loadPromise !== null) {
        return loadPromise;
    }

    const loader = getGoogleMapsLoader(locale);

    loadPromise = Promise.all([
        loader.importLibrary("places"),
        loader.importLibrary("maps"),
        loader.importLibrary("geocoding"),
    ])
        .then(() => {
            isLoaded = true;
            return window.google;
        })
        .catch((error) => {
            Logger.error(ServiceType.GOOGLE, "Failed to load Google Maps libraries", {
                tag: LogTag.STORES,
                error,
            });
            loadPromise = null;
            throw error;
        });

    return loadPromise;
};

export const googleMapsUtils = {
    loadMaps: loadGoogleMaps,
    isLoaded: (): boolean => isLoaded && !!window.google?.maps,
};
